import { supabase } from "../config/supabase.ts";
import * as FinancialTypes from "../types/unified-financial.ts";
import { BillingPeriodUtils } from "../utils/billingPeriod.ts";
import * as SupabaseTypes from "../types/supabase.ts";
import { PackageService } from "./PackageService.ts";
import { isDateOnlyString, toDateOnlyString } from '../utils/dateHelpers';

// Alias para os tipos Supabase
type SupabaseFinancialProfileRow = SupabaseTypes.SupabaseFinancialProfileRow;
type SupabasePackageRow = SupabaseTypes.SupabasePackageRow;
type SupabasePatientWithUser = SupabaseTypes.SupabasePatientWithUser;
type SupabaseInvoiceRow = SupabaseTypes.SupabaseInvoiceRow;
type SupabaseAppointmentRow = SupabaseTypes.SupabaseAppointmentRow;
type SupabasePatientRow = SupabaseTypes.SupabasePatientRow;
type SupabaseUserRow = SupabaseTypes.SupabaseUserRow;

// Alias para os tipos Financeiros (já existentes)
// ... (o resto dos aliases que já criamos)

// Alias para os tipos importados para evitar refatoração em massa no arquivo
type UnifiedPatient = FinancialTypes.UnifiedPatient;
type FinancialProfile = FinancialTypes.FinancialProfile;
type Appointment = FinancialTypes.Appointment;
type Invoice = FinancialTypes.Invoice;
type SessionsContracted = FinancialTypes.SessionsContracted;
type ValidityType = FinancialTypes.ValidityType;
type AppointmentSummary = FinancialTypes.AppointmentSummary;
type AppointmentSummaryType = FinancialTypes.AppointmentSummaryType;
type RenewalDecision = FinancialTypes.RenewalDecision;
type RenewalStatus = FinancialTypes.RenewalStatus;

export class PatientService {
  private static readonly COLLECTION_NAME = 'patients';
  private static patientRgColumnAvailable: boolean | null = null;

  private static getPatientSelect(includeRg?: boolean): string {
    const shouldIncludeRg = includeRg ?? (this.patientRgColumnAvailable !== false);

    return `
      patientId,
      patientCpf,
      ${shouldIncludeRg ? 'patientRg,' : ''}
      patientFullName,
      patientDateOfBirth,
      patientGender,
      patientAddressStreet,
      patientAddressNumber,
      patientAddressComplement,
      patientAddressNeighborhood,
      patientAddressCity,
      patientAddressState,
      patientAddressZipCode,
      patientEmergencyContactName,
      patientEmergencyContactPhone,
      patientEmergencyContactRelationship,
      patientMedicalInfoBloodType,
      patientMedicalInfoAllergies,
      patientMedicalInfoMedications,
      patientMedicalInfoConditions,
      patientLifecycleStatus,
      patientCreatedAt,
      patientUpdatedAt,
      users!patients_userId_fkey ( userId, userEmail, userPhoneNumber )
    `;
  }

  private static isMissingPatientRgColumn(error: any): boolean {
    return (
      !!error &&
      error.code === "42703" &&
      typeof error.message === "string" &&
      error.message.includes("patientRg")
    );
  }

  private static markPatientRgColumnUnavailable(): void {
    if (this.patientRgColumnAvailable !== false) {
      console.warn('⚠️ Coluna patients.patientRg não encontrada no schema atual. Reexecutando operação sem RG.');
    }

    this.patientRgColumnAvailable = false;
  }

  private static stripUnsupportedPatientRg<T extends Record<string, any>>(payload: T): Omit<T, "patientRg"> {
    const { patientRg, ...payloadWithoutRg } = payload;
    return payloadWithoutRg;
  }

  /**
   * Busca todos os pacientes da coleção existente
   * ✅ Sempre retorna estruturas financeiras válidas com valores padrão
   */
  static async getAllPatients(clientId?: string): Promise<UnifiedPatient[]> {
    try {
      let scopedPatientIds: string[] | null = null;
      if (clientId) {
        const { data: patientLinks, error: linkError } = await supabase
          .from("patient_clients")
          .select("patientId")
          .eq("clientId", clientId);

        if (linkError) {
          console.error('Erro ao buscar vínculos de pacientes por tenant:', linkError);
          throw new Error(`Erro ao buscar vínculos de pacientes: ${linkError.message}`);
        }

        scopedPatientIds = [...new Set((patientLinks || []).map(link => link.patientId).filter(Boolean))];
        if (scopedPatientIds.length === 0) {
          return [];
        }
      }

      const fetchPatients = async (includeRg: boolean) => {
        let patientsQuery = supabase
          .from(PatientService.COLLECTION_NAME)
          .select(this.getPatientSelect(includeRg));

        if (scopedPatientIds) {
          patientsQuery = patientsQuery.in("patientId", scopedPatientIds);
        }

        return await patientsQuery as { data: SupabasePatientWithUser[] | null; error: any };
      };

      let { data, error } = await fetchPatients(this.patientRgColumnAvailable !== false);

      if (this.isMissingPatientRgColumn(error)) {
        this.markPatientRgColumnUnavailable();
        ({ data, error } = await fetchPatients(false));
      } else if (!error && this.patientRgColumnAvailable === null) {
        this.patientRgColumnAvailable = true;
      }

      if (error) {
        console.error('Erro ao buscar pacientes do Supabase:', error);
        throw new Error(`Erro ao buscar pacientes do Supabase: ${error.message}`);
      }

      if (!data) {
        return [];
      }

      let patients: UnifiedPatient[] = data.map((patientData: any) => {
        return this.mapToUnifiedPatient(patientData);
      });

      patients = await this.attachFinancialData(patients, clientId);
      patients = await this.attachInvoiceData(patients, clientId); // Novo método
      return await this.attachAppointmentData(patients, clientId);
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
      let errorMessage = "Erro desconhecido ao buscar pacientes.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao buscar pacientes: ${errorMessage}`);
    }

  }

  private static mapToUnifiedPatient(patientData: SupabasePatientWithUser): UnifiedPatient {
    // Extrair o objeto de usuário, se for um array, pegar o primeiro elemento
    const user = Array.isArray(patientData.users) ? patientData.users[0] : patientData.users;

    const personalData = {
      name: patientData.patientFullName || 'Nome não informado',
      email: user?.userEmail || '',
      phone: user?.userPhoneNumber || '',
      cpf: patientData.patientCpf || '',
      rg: patientData.patientRg || '',
      birthDate: patientData.patientDateOfBirth || ''
    };

    // Inicializa financialProfile com valores padrão.
    // Os dados financeiros reais (packages, financial_profiles) serão buscados por attachFinancialData.
    const financialProfile: FinancialProfile = {
      active: true,
      hasPackage: false,
      individualSessionPrice: 150,
      absenceControl: {
        totalAbsences: 0,
        unexcusedAbsences: 0,
        chargeableAbsences: 0,
        absenceHistory: [],
        lastResetDate: new Date(0).toISOString() // Data inicial padrão
      },
      // Outros campos de financialProfile serão preenchidos ou permanecerão undefined
    };

    return {
      id: patientData.patientId,
      personalData,
      financialProfile,
      patientLifecycleStatus: patientData.patientLifecycleStatus || 'active',
      lifecycleStatus: patientData.patientLifecycleStatus || 'active',
      appointments: [], // Será populado por attachAppointmentData
      invoices: [], // Será populado por attachInvoiceData (novo método)
      createdAt: patientData.patientCreatedAt || new Date().toISOString(),
      updatedAt: patientData.patientUpdatedAt || new Date().toISOString()
    };
  }

  private static normalizeText(text?: string): string {
    if (!text) {
      return '';
    }
    return text
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private static toDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return new Date(value);
    }
    if (typeof value.toDate === 'function') {
      const converted = value.toDate();
      return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private static calculateAppointmentPeriod(patient: UnifiedPatient): {
    start: Date;
    end: Date;
    periodType: AppointmentSummaryType;
  } {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    if (patient.financialProfile.hasPackage && patient.financialProfile.packageInfo) {
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const { startDate, endDate } = BillingPeriodUtils.calculateBillingPeriod(patient, currentMonth);

      return {
        start: startDate,
        end: endDate,
        periodType: 'package_cycle'
      };
    }

    return {
      start: startOfMonth,
      end: endOfMonth,
      periodType: 'monthly'
    };
  }

  private static async attachFinancialData(patients: UnifiedPatient[], clientId?: string): Promise<UnifiedPatient[]> {
    if (patients.length === 0) {
      return patients;
    }

    const patientIds = patients.map(p => p.id);

    // Buscar financial_profiles
    let financialProfilesQuery = supabase
      .from("financial_profiles")
      .select(
        `
        financialProfileId,
        clientId,
        patientId,
        financialProfileIsActive,
        financialProfileHasPackage,
        financialProfileIndividualSessionPrice,
        financialProfileRenewalStatus,
        financialProfileObitoDate,
        financialProfileCreatedAt,
        financialProfileUpdatedAt
        `
      )
      .in("patientId", patientIds);

    if (clientId) {
      financialProfilesQuery = financialProfilesQuery.eq("clientId", clientId);
    }

    const { data: financialProfilesData, error: fpError } = await financialProfilesQuery as { data: SupabaseFinancialProfileRow[] | null; error: any }; // Explicitamente tipado

    if (fpError) {
      console.error("❌ Erro ao carregar perfis financeiros do Supabase:", fpError);
      throw new Error(`Erro ao carregar perfis financeiros: ${fpError.message}`);
    }

    let packagesData: SupabasePackageRow[] | null = [];
    if (financialProfilesData && financialProfilesData.length > 0) {
      let packagesQuery = supabase
        .from("packages")
        .select(
          `
          packageId,
          clientId,
          financialProfileId,
          packageName,
          packagePrice,
          packageSessionsContracted,
          packageSessionsUsed,
          packageSessionsRemaining,
          packageValidityType,
          packageCustomValidityMonths,
          packagePaymentDate,
          packageRenewalDate,
          packageNotes,
          packageCreatedAt
          `
        )
        .in("financialProfileId", financialProfilesData.map(fp => fp.financialProfileId));

      if (clientId) {
        packagesQuery = packagesQuery.eq("clientId", clientId);
      }

      const { data: packagesRows, error: pkgError } = await packagesQuery as { data: SupabasePackageRow[] | null; error: any };

      if (pkgError) {
        console.error("❌ Erro ao carregar pacotes do Supabase:", pkgError);
        throw new Error(`Erro ao carregar pacotes: ${pkgError.message}`);
      }

      packagesData = packagesRows;
    }

    // Mapear packages para financialProfiles
    const packagesMap = new Map<string, SupabasePackageRow>();
    packagesData?.forEach((pkg) => packagesMap.set(pkg.financialProfileId, pkg));

    // Mapear financial_profiles para pacientes
    const financialProfileMap = new Map<string, FinancialProfile>();
    financialProfilesData?.forEach((fp) => {
      const packageData = packagesMap.get(fp.financialProfileId);
      const financialProfile: FinancialProfile = {
        financialProfileId: fp.financialProfileId,
        clientId: fp.clientId, // << ADICIONAR ESTA LINHA!
        active: fp.financialProfileIsActive,
        hasPackage: fp.financialProfileHasPackage,
        individualSessionPrice: fp.financialProfileIndividualSessionPrice,
        absenceControl: {
          totalAbsences: 0,
          unexcusedAbsences: 0,
          chargeableAbsences: 0,
          absenceHistory: [],
          lastResetDate: new Date(0).toISOString()
        }, // Por enquanto, mantemos como padrão, pois AbsenceControl não está diretamente na tabela financial_profiles
        renewalStatus: fp.financialProfileRenewalStatus as RenewalStatus,
        obitoDate: fp.financialProfileObitoDate
      };

      if (packageData) {
        financialProfile.packageInfo = {
          packageId: packageData.packageId,
          clientId: packageData.clientId,
          financialProfileId: packageData.financialProfileId,
          packageName: packageData.packageName || undefined,
          packagePrice: packageData.packagePrice || 0,
          packageSessionsContracted: packageData.packageSessionsContracted as number,
          packageSessionsUsed: packageData.packageSessionsUsed,
          packageSessionsRemaining: packageData.packageSessionsRemaining,
          packageValidityType: packageData.packageValidityType as ValidityType,
          packageCustomValidityMonths: packageData.packageCustomValidityMonths || undefined,
          packagePaymentDate: packageData.packagePaymentDate ?? new Date().toISOString(), // Garante que nunca seja nulo
          packageRenewalDate: packageData.packageRenewalDate || undefined,
          packageNotes: packageData.packageNotes || undefined,
          packageCreatedAt: packageData.packageCreatedAt ?? new Date().toISOString(), // Garante que nunca seja nulo
        };
        financialProfile.hasPackage = true;
      }
      financialProfileMap.set(fp.patientId, financialProfile);
    });

    return patients.map(patient => ({
      ...patient,
      financialProfile: financialProfileMap.get(patient.id) || patient.financialProfile, // Sobrescreve com dados do Supabase ou mantém o padrão
    }));
  }

  private static async attachInvoiceData(patients: UnifiedPatient[], clientId?: string): Promise<UnifiedPatient[]> {
    if (patients.length === 0) {
      return patients;
    }

    const patientIds = patients.map(p => p.id);

    let invoicesQuery = supabase
      .from("invoices")
      .select(
        `
        invoiceId,
        clientId,
        patientId,
        invoiceMonthReference,
        invoiceTotalAmount,
        invoiceStatus,
        invoiceDueDate,
        invoicePaidAt,
        invoiceNotes,
        invoiceCreatedAt
        `
      )
      .in("patientId", patientIds);

    if (clientId) {
      invoicesQuery = invoicesQuery.eq("clientId", clientId);
    }

    const { data: invoicesData, error: invError } = await invoicesQuery;

    if (invError) {
      console.error("❌ Erro ao carregar faturas do Supabase:", invError);
      throw new Error(`Erro ao carregar faturas: ${invError.message}`);
    }

    const invoiceMap = new Map<string, Invoice[]>();
    invoicesData?.forEach(inv => {
      const existingInvoices = invoiceMap.get(inv.patientId) || [];
      existingInvoices.push({
        invoiceId: inv.invoiceId,
        month: inv.invoiceMonthReference,
        type: 'individual', // Tipo padrão, pode ser ajustado com base na lógica
        items: [], // Itens da fatura serão carregados separadamente ou inferidos
        total: inv.invoiceTotalAmount,
        status: inv.invoiceStatus,
        createdAt: inv.invoiceCreatedAt,
        dueDate: inv.invoiceDueDate ? (isDateOnlyString(inv.invoiceDueDate) ? inv.invoiceDueDate : toDateOnlyString(new Date(inv.invoiceDueDate))) : undefined,
        paidAt: inv.invoicePaidAt,
        notes: inv.invoiceNotes,
      });
      invoiceMap.set(inv.patientId, existingInvoices);
    });

    return patients.map(patient => ({
      ...patient,
      invoices: invoiceMap.get(patient.id) || [],
    }));
  }

  private static async attachAppointmentData(patients: UnifiedPatient[], clientId?: string): Promise<UnifiedPatient[]> {
    if (patients.length === 0) {
      return patients;
    }


    const periods = new Map<string, { start: Date; end: Date; periodType: AppointmentSummaryType }>();
    let globalStart: Date | null = null;
    let globalEnd: Date | null = null;

    patients.forEach(patient => {
      const period = this.calculateAppointmentPeriod(patient);
      periods.set(patient.id, period);

      if (!globalStart || period.start < globalStart) {
        globalStart = new Date(period.start);
      }
      if (!globalEnd || period.end > globalEnd) {
        globalEnd = new Date(period.end);
      }
    });

    if (!globalStart || !globalEnd || globalStart > globalEnd) {
      return patients;
    }

    // Preparar um mapa de pacientes para fácil acesso pelo ID ou nome normalizado
    const patientMap = new Map<string, UnifiedPatient>();
    patients.forEach(patient => {
      patientMap.set(patient.id, patient);
      const normalizedName = this.normalizeText(patient.personalData.name);
      if (normalizedName) {
        patientMap.set(normalizedName, patient);
      }
    });

    if (!globalStart || !globalEnd || globalStart > globalEnd) {
      return patients;
    }

    const confirmedGlobalStart: Date = globalStart;
    const confirmedGlobalEnd: Date = globalEnd;

    const fetchAppointments = async (notesColumn: "appointmentResolutionNotes" | "appointmentNotes") => {
      let query = supabase
        .from("appointments")
        .select(`
          appointmentId,
          appointmentStartDate,
          appointmentEndDate,
          appointmentStatus,
          appointmentLocation,
          appointmentTitle,
          ${notesColumn},
          appointmentCreatedAt,
          appointmentUpdatedAt,
          patientId
        `)
        .gte("appointmentStartDate", confirmedGlobalStart.toISOString())
        .lte("appointmentStartDate", confirmedGlobalEnd.toISOString());

      if (clientId) {
        query = query.eq("clientId", clientId);
      }

      return query;
    };

    let { data: appointmentsData, error } = await fetchAppointments("appointmentResolutionNotes");

    // Compatibilidade com schemas antigos que ainda usam appointmentNotes
    if (error && error.code === "42703" && String(error.message).includes("appointmentResolutionNotes")) {
      const fallback = await fetchAppointments("appointmentNotes");
      appointmentsData = fallback.data;
      error = fallback.error;
    }

    if (error) {

      console.error("❌ Erro ao carregar agendamentos do Supabase:", error);
      let errorMessage = "Erro desconhecido ao carregar agendamentos.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao carregar agendamentos: ${errorMessage}`);
    }

    const appointmentsByPatient = new Map<string, Appointment[]>();

    if (appointmentsData) {
        appointmentsData.forEach(appointmentRow => {
      const startDate = new Date(appointmentRow.appointmentStartDate);
      const endDate = appointmentRow.appointmentEndDate ? new Date(appointmentRow.appointmentEndDate) : startDate;

      const targetPatientId: string | undefined = appointmentRow.patientId; // Agora confiamos que patientId virá do banco ou será tratado antes

      // Fallback (mantido por segurança, mas deve ser menos usado após a migração)
      if (!targetPatientId) {
        const candidateText = `${appointmentRow.appointmentTitle || ''}`.trim(); // Ajustado para não depender de patientFullName no SELECT
        if (candidateText) {
          const normalizedSubject = this.normalizeText(candidateText);
          for (const [key, patient] of patientMap.entries()) {
            const normalizedPatientName = this.normalizeText(patient.personalData.name);
            if (
              normalizedSubject.includes(normalizedPatientName) ||
              normalizedPatientName.includes(normalizedSubject)
            ) {
              // TODO: Se isso acontecer, significa que o agendamento não está bem vinculado por patientId. Investigar.
              // Por enquanto, apenas para associar corretamente.
              (patient.appointments = patient.appointments || []).push({
                appointmentId: appointmentRow.appointmentId,
                date: startDate.toISOString(),
                status: appointmentRow.appointmentStatus,
                duration: Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000)),
                notes: (appointmentRow as any).appointmentResolutionNotes ||
                       (appointmentRow as any).appointmentNotes ||
                       appointmentRow.appointmentTitle || 
                       appointmentRow.appointmentLocation || 
                       '',
                createdAt: new Date(appointmentRow.appointmentCreatedAt).toISOString(),
                updatedAt: new Date(appointmentRow.appointmentUpdatedAt).toISOString()
              });
              return; // Pula para o próximo appointmentRow
            }
          }
        }
      }

      if (!targetPatientId) {
        return; // Pular agendamentos que não podem ser vinculados a um paciente conhecido
      }

      const appointment: Appointment = {
        appointmentId: appointmentRow.appointmentId,
        date: startDate.toISOString(),
        status: appointmentRow.appointmentStatus,
        duration: Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000)),
        notes: (appointmentRow as any).appointmentResolutionNotes ||
               (appointmentRow as any).appointmentNotes ||
               appointmentRow.appointmentTitle || 
               appointmentRow.appointmentLocation || 
               '',
        createdAt: new Date(appointmentRow.appointmentCreatedAt).toISOString(),
        updatedAt: new Date(appointmentRow.appointmentUpdatedAt).toISOString()
      };

      const existing = appointmentsByPatient.get(targetPatientId) ?? [];
      existing.push(appointment);
      appointmentsByPatient.set(targetPatientId, existing);
    });
  }

    return patients.map(patient => {
      const period = periods.get(patient.id)!;
      const allAppointments = (appointmentsByPatient.get(patient.id) ?? []).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      const appointmentsInPeriod = allAppointments.filter(appointment => {
        const appointmentDate = new Date(appointment.date);
        return appointmentDate >= period.start && appointmentDate <= period.end;
      });

      const summary: AppointmentSummary = {
        periodStart: period.start.toISOString(),
        periodEnd: period.end.toISOString(),
        periodType: period.periodType,
        total: appointmentsInPeriod.length,
        scheduled: appointmentsInPeriod.filter(apt => apt.status === 'scheduled').length,
        completed: appointmentsInPeriod.filter(apt => apt.status === 'completed').length,
        cancelled: appointmentsInPeriod.filter(apt => apt.status === 'cancelled').length,
        noShow: appointmentsInPeriod.filter(apt => apt.status === 'no_show').length
      };

      return {
        ...patient,
        appointments: allAppointments,
        appointmentSummary: summary
      };
    });
  }

  /**
   * Busca um paciente específico
   * ✅ Sempre retorna estrutura financeira válida com valores padrão
   */
  static async getPatientById(patientId: string, clientId?: string): Promise<UnifiedPatient | null> {
    try {
      if (clientId) {
        const { data: patientLink, error: linkError } = await supabase
          .from("patient_clients")
          .select("patientId")
          .eq("patientId", patientId)
          .eq("clientId", clientId)
          .maybeSingle();

        if (linkError) {
          console.error("❌ Erro ao validar vínculo do paciente com tenant:", linkError);
          throw new Error(`Erro ao validar vínculo do paciente: ${linkError.message}`);
        }

        if (!patientLink) {
          return null;
        }
      }

      const fetchPatient = async (includeRg: boolean) => {
        return await supabase
          .from(PatientService.COLLECTION_NAME)
          .select(this.getPatientSelect(includeRg))
          .eq("patientId", patientId)
          .single() as { data: SupabasePatientWithUser | null; error: any };
      };

      let { data, error } = await fetchPatient(this.patientRgColumnAvailable !== false);

      if (this.isMissingPatientRgColumn(error)) {
        this.markPatientRgColumnUnavailable();
        ({ data, error } = await fetchPatient(false));
      } else if (!error && this.patientRgColumnAvailable === null) {
        this.patientRgColumnAvailable = true;
      }

      if (error) {
        console.error("❌ Erro ao buscar paciente do Supabase:", error);
        let errorMessage = "Erro desconhecido ao buscar paciente.";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null && 'message' in error) {
          errorMessage = (error as { message: string }).message;
        }
        throw new Error(`Erro ao buscar paciente: ${errorMessage}`);
      }

      if (!data) {
        return null;
      }

      let patient: UnifiedPatient = this.mapToUnifiedPatient(data as SupabasePatientWithUser); // Tipagem explícita

      const patientsWithFinancials = await this.attachFinancialData([patient], clientId);
      const patientsWithInvoices = await this.attachInvoiceData(patientsWithFinancials, clientId);
      const patientsWithAppointments = await this.attachAppointmentData(patientsWithInvoices, clientId);

      return patientsWithAppointments[0] ?? null;
    } catch (error) {
      console.error("Erro ao buscar paciente:", error);
      let errorMessage = "Erro desconhecido ao buscar paciente.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao buscar paciente: ${errorMessage}`);
    }
  }

  /**
   * Atualiza o perfil financeiro de um paciente, delegando a gestão de pacotes.
   */
  static async updateFinancialProfile(patientId: string, financialProfile: Partial<FinancialProfile>): Promise<void> {
    try {
      const { clientId } = financialProfile;
      if (!clientId) {
        throw new Error("ClientId é obrigatório para atualizar o perfil financeiro.");
      }

      // Passo 1: Tenta encontrar um perfil financeiro existente
      let { data: existingProfile, error: fetchError } = await supabase
        .from("financial_profiles")
        .select("financialProfileId")
        .eq("clientId", clientId)
        .eq("patientId", patientId)
        .single();

      // Se houver um erro que não seja "nenhum resultado encontrado", lance o erro.
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error("Erro ao buscar perfil financeiro existente:", fetchError);
        throw new Error(`Erro ao buscar perfil financeiro: ${fetchError.message}`);
      }

      let financialProfileId: string;

      const profileData = {
        clientId: clientId,
        patientId: patientId,
        financialProfileIsActive: financialProfile.active,
        financialProfileHasPackage: financialProfile.hasPackage,
        financialProfileIndividualSessionPrice: financialProfile.individualSessionPrice,
        financialProfileUpdatedAt: new Date().toISOString(),
      };

      if (existingProfile) {
        // Passo 2a: Se existe, atualiza
        const { data, error } = await supabase
          .from("financial_profiles")
          .update(profileData)
          .eq("financialProfileId", existingProfile.financialProfileId)
          .select("financialProfileId")
          .single();
        
        if (error) throw new Error(`Erro ao atualizar perfil financeiro: ${error.message}`);
        financialProfileId = data!.financialProfileId;
        console.log("✅ Perfil financeiro ATUALIZADO com sucesso.");
      } else {
        // Passo 2b: Se não existe, cria
        const { data, error } = await supabase
          .from("financial_profiles")
          .insert(profileData)
          .select("financialProfileId")
          .single();

        if (error) throw new Error(`Erro ao criar perfil financeiro: ${error.message}`);
        financialProfileId = data!.financialProfileId;
        console.log("✅ Perfil financeiro CRIADO com sucesso.");
      }

      // Passo 3: Gerencia o pacote
      if (financialProfile.hasPackage && financialProfile.packageInfo) {
        const { packageId, ...packageData } = financialProfile.packageInfo;
        const packagePayload = { ...packageData, financialProfileId, clientId };

        if (packageId && !packageId.startsWith("pkg_")) {
          await PackageService.updatePackage(packageId, packagePayload, clientId);
        } else {
          // Quando um NOVO pacote é criado, chamamos o serviço que agora
          // é responsável por criar o pacote E o faturamento correspondente.
          await PackageService.createPackage(packagePayload);
        }
      }

    } catch (error: any) {
      console.error("❌ Erro ao atualizar perfil financeiro:", error);
      throw new Error(`Erro ao atualizar perfil financeiro: ${error.message}`);
    }
  }

  /**
   * Adiciona uma fatura ao paciente
   */
  static async addInvoice(patientId: string, invoice: Invoice): Promise<void> {
    try {
      // Precisamos obter o clientId do paciente para vincular a fatura corretamente
      const patient = await this.getPatientById(patientId);
      if (!patient) {
        throw new Error('Paciente não encontrado');
      }

      // Assumindo que o clientId está disponível no financialProfile do paciente
      // TODO: Pode ser necessário buscar o clientId de forma mais robusta se não estiver sempre presente
      const clientId = patient.financialProfile.clientId; 
      if (!clientId) {
        throw new Error('ClientId não encontrado para o paciente.');
      }

      const newInvoiceSupabase: SupabaseInvoiceRow = {
        invoiceId: invoice.invoiceId || crypto.randomUUID(), // Se `invoice.invoiceId` já existe, usa ele, senão gera um novo
        clientId: clientId,
        patientId: patientId,
        invoiceMonthReference: invoice.month,
        invoiceTotalAmount: invoice.total,
        invoiceStatus: invoice.status as SupabaseInvoiceRow["invoiceStatus"],
        invoiceDueDate: invoice.dueDate || undefined, // Converte null para undefined
        invoicePaidAt: invoice.paidAt || undefined,   // Converte null para undefined
        invoiceNotes: invoice.notes || undefined,     // Converte null para undefined
        invoiceCreatedAt: new Date().toISOString(),
      };

      const { error } = await supabase.from("invoices").insert(newInvoiceSupabase);

      if (error) {
        console.error('❌ Erro ao adicionar fatura no Supabase:', error);
        let errorMessage = "Erro desconhecido ao adicionar fatura.";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null && 'message' in error) {
          errorMessage = (error as { message: string }).message;
        }
        throw new Error(`Erro ao adicionar fatura: ${errorMessage}`);
      }

      console.log('✅ Fatura adicionada com sucesso no Supabase');

    } catch (error) {
      console.error('Erro ao adicionar fatura:', error);
      let errorMessage = "Erro desconhecido ao adicionar fatura.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao adicionar fatura: ${errorMessage}`);
    }
  }

  /**
   * Calcula métricas mensais
   */
  static async calculateMonthlyMetrics(month: string, clientId?: string): Promise<FinancialTypes.FinancialMetrics> {
    try {
      const patients = await this.getAllPatients(clientId); // Já refatorado para usar Supabase
      
      const activePatients = patients.filter(p => p.financialProfile?.active === true);
      const patientsWithPackage = activePatients.filter(p => p.financialProfile?.hasPackage === true);
      
      let packageRevenue = 0;
      let individualRevenue = 0;
      let pending = 0;
      let overdue = 0;
      let actualRevenueTotal = 0;

      // Calcular receitas
      for (const patient of activePatients) {
        if (patient.financialProfile.hasPackage && patient.financialProfile.packageInfo) {
          // Corrigido para packagePrice
          packageRevenue += patient.financialProfile.packageInfo.packagePrice || 0; 
        }
        
        // Calcular pendências e receita realizada
        for (const invoice of patient.invoices) {
          if (invoice.status === 'pending') {
            pending += invoice.total;
          } else if (invoice.status === 'overdue') {
            overdue += invoice.total;
          } else if (invoice.status === 'paid') {
            actualRevenueTotal += invoice.total;
          }
        }
      }

      // Retorna o objeto completo, satisfazendo a interface FinancialMetrics
      return {
        month: month,
        totalPatients: patients.length,
        activePatients: activePatients.length,
        patientsWithPackage: patientsWithPackage.length,
        estimatedRevenue: {
          packages: packageRevenue,
          individuals: individualRevenue, // Lógica para isso pode ser adicionada depois
          total: packageRevenue + individualRevenue
        },
        actualRevenue: {
          packages: 0, // Placeholder
          individuals: 0, // Placeholder
          total: actualRevenueTotal
        },
        pending,
        overdue,
        lastUpdated: new Date().toISOString()
      };

    } catch (error: any) {
      console.error("Erro ao calcular métricas:", error);
      throw new Error(`Erro ao calcular métricas: ${error.message}`);
    }
  }

  /**
   * Cria um novo paciente no Supabase.
   * Este método deve ser usado para criar pacientes com as estruturas esperadas pelo Supabase.
   * Retorna o patientId do paciente criado.
   * @param personalData Dados pessoais do paciente, incluindo email e userPhoneNumber que podem ser usados para criar/vincular um usuário.
   * @param clientId O ID do cliente (tenant) ao qual este paciente pertencerá.
   */
  static async createPatient(personalData: Partial<SupabasePatientRow & { userEmail: string; userPhoneNumber: string }>, clientId: string): Promise<string> {
    try {
      let userId: string | undefined;
      if (personalData.userEmail) {
        const { data: existingUser, error: fetchUserError } = await supabase
          .from("users")
          .select("userId")
          .eq("userEmail", personalData.userEmail)
          .single();

        if (fetchUserError && fetchUserError.code === "PGRST116") { // Nenhuma linha encontrada
          // Usuário não existe, criar um novo
          const { data: newUser, error: newUserError } = await supabase
            .from("users")
            .insert({
              userEmail: personalData.userEmail,
              userDisplayName: personalData.patientFullName, // Usar nome completo do paciente como display name
              userPhoneNumber: personalData.userPhoneNumber,
              userCreatedAt: new Date().toISOString(),
            })
            .select("userId")
            .single();

          if (newUserError) {
            console.error("❌ Erro ao criar novo usuário para paciente:", newUserError);
            throw new Error(`Erro ao criar usuário: ${newUserError.message}`);
          }
          userId = newUser?.userId;
        } else if (fetchUserError) {
          console.error("❌ Erro ao buscar usuário existente para paciente:", fetchUserError);
          throw new Error(`Erro ao buscar usuário: ${fetchUserError.message}`);
        } else {
          userId = existingUser?.userId;
        }
      }

      // 2. Inserir na tabela `patients`
      const patientToInsert: Partial<SupabasePatientRow> = {
        userId: userId,
        patientCpf: personalData.patientCpf,
        patientRg: personalData.patientRg,
        patientFullName: personalData.patientFullName,
        patientDateOfBirth: personalData.patientDateOfBirth,
        patientGender: personalData.patientGender,
        patientAddressStreet: personalData.patientAddressStreet,
        patientAddressNumber: personalData.patientAddressNumber,
        patientAddressComplement: personalData.patientAddressComplement,
        patientAddressNeighborhood: personalData.patientAddressNeighborhood,
        patientAddressCity: personalData.patientAddressCity,
        patientAddressState: personalData.patientAddressState,
        patientAddressZipCode: personalData.patientAddressZipCode,
        patientEmergencyContactName: personalData.patientEmergencyContactName,
        patientEmergencyContactPhone: personalData.patientEmergencyContactPhone,
        patientEmergencyContactRelationship: personalData.patientEmergencyContactRelationship,
        patientMedicalInfoBloodType: personalData.patientMedicalInfoBloodType,
        patientMedicalInfoAllergies: personalData.patientMedicalInfoAllergies,
        patientMedicalInfoMedications: personalData.patientMedicalInfoMedications,
        patientMedicalInfoConditions: personalData.patientMedicalInfoConditions,
        patientCreatedAt: new Date().toISOString(),
        patientUpdatedAt: new Date().toISOString(),
      };

      const insertPatient = async (includeRg: boolean) => {
        const payload = includeRg
          ? patientToInsert
          : this.stripUnsupportedPatientRg(patientToInsert);

        return await supabase
          .from("patients")
          .insert(payload)
          .select("patientId")
          .single();
      };

      let { data: newPatient, error: patientError } = await insertPatient(this.patientRgColumnAvailable !== false);

      if (this.isMissingPatientRgColumn(patientError)) {
        this.markPatientRgColumnUnavailable();
        ({ data: newPatient, error: patientError } = await insertPatient(false));
      } else if (!patientError && this.patientRgColumnAvailable === null) {
        this.patientRgColumnAvailable = true;
      }

      if (patientError) {
        console.error("❌ Erro ao criar paciente no Supabase:", patientError);
        throw new Error(`Erro ao criar paciente: ${patientError.message}`);
      }

      const patientId = newPatient?.patientId;
      if (!patientId) {
        throw new Error("Não foi possível obter o patientId do paciente recém-criado.");
      }

      // 3. Vincular o paciente ao cliente (tenant) na tabela patient_clients
      const { error: patientClientError } = await supabase
        .from("patient_clients")
        .insert({
          patientId: patientId,
          clientId: clientId,
          createdAt: new Date().toISOString(),
        });
      
      if (patientClientError) {
        console.error("❌ Erro ao vincular paciente ao cliente:", patientClientError);
        throw new Error(`Erro ao vincular paciente ao cliente: ${patientClientError.message}`);
      }

      // 4. Criar um financial_profile básico para o paciente
      const { error: fpInsertError } = await supabase
        .from("financial_profiles")
        .insert({
          patientId: patientId,
          clientId: clientId,
          financialProfileIsActive: true,
          financialProfileHasPackage: false,
          financialProfileIndividualSessionPrice: 150, // Valor padrão
          financialProfileCreatedAt: new Date().toISOString(),
          financialProfileUpdatedAt: new Date().toISOString(),
        });

      if (fpInsertError) {
        console.error("❌ Erro ao criar financial profile para paciente:", fpInsertError);
        throw new Error(`Erro ao criar financial profile: ${fpInsertError.message}`);
      }

      console.log(`✅ Paciente ${patientId} criado com sucesso no Supabase.`);
      return patientId;

    } catch (error) {
      console.error("Erro ao criar paciente:", error);
      let errorMessage = "Erro desconhecido ao criar paciente.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao criar paciente: ${errorMessage}`);
    }
  }
  
/**
 * Atualiza partes específicas do perfil financeiro de um paciente.
 * Este método foi adaptado para interagir com as tabelas `financial_profiles` e `packages` do Supabase.
 * Ele tenta atualizar ambos os conjuntos de dados (financialProfile e packageInfo) com base nas `updates` fornecidas.
 * @param patientId O ID do paciente.
 * @param updates Um objeto contendo as atualizações a serem aplicadas, com chaves que podem indicar campos de package ou packageInfo.
 */
  static async updatePatientFinancialProfile(
    patientId: string,
    updates: Record<string, any>
  ): Promise<void> {
    try {
      // Obter o perfil financeiro atual e o pacote para determinar o que atualizar
      const patient = await this.getPatientById(patientId);
      if (!patient) {
        throw new Error("Paciente não encontrado.");
      }

      const financialProfileId = patient.financialProfile.financialProfileId; // Assumindo que o financialProfileId existe na UnifiedPatient
      if (!financialProfileId) {
        throw new Error("FinancialProfileId não encontrado para o paciente.");
      }

      const currentPackageId = patient.financialProfile.packageInfo?.packageId;
      const packageClientId = patient.financialProfile.clientId;

      const financialProfileUpdates: Partial<SupabaseFinancialProfileRow> = {};
      const packageUpdates: Partial<SupabasePackageRow> = {};
      let shouldUpdatePackage = false;

      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined) continue; // Ignorar valores undefined

        // Mapear campos de FinancialProfile para SupabaseFinancialProfileRow
        if (key === 'active') financialProfileUpdates.financialProfileIsActive = value;
        if (key === 'hasPackage') financialProfileUpdates.financialProfileHasPackage = value;
        if (key === 'individualSessionPrice') financialProfileUpdates.financialProfileIndividualSessionPrice = value;
        if (key === 'renewalStatus') financialProfileUpdates.financialProfileRenewalStatus = value;
        if (key === 'obitoDate') financialProfileUpdates.financialProfileObitoDate = value;

        // Mapear campos de PackageInfo para SupabasePackageRow
        if (key.startsWith('packageInfo.')) {
          shouldUpdatePackage = true;
          const field = key.slice('packageInfo.'.length);
          if (field === 'packageName') packageUpdates.packageName = value;
          if (field === 'price') packageUpdates.packagePrice = value;
          if (field === 'sessionsContracted') packageUpdates.packageSessionsContracted = value;
          if (field === 'sessionsUsed') packageUpdates.packageSessionsUsed = value;
          if (field === 'sessionsRemaining') packageUpdates.packageSessionsRemaining = value;
          if (field === 'validityType') packageUpdates.packageValidityType = value;
          if (field === 'customValidityMonths') packageUpdates.packageCustomValidityMonths = value;
          if (field === 'paymentDate') packageUpdates.packagePaymentDate = value;
          if (field === 'renewalDate') packageUpdates.packageRenewalDate = value;
          if (field === 'notes') packageUpdates.packageNotes = value;
        }
      }

      financialProfileUpdates.financialProfileUpdatedAt = new Date().toISOString();

      // 1. Atualizar financial_profiles
      const { error: fpUpdateError } = await supabase
        .from("financial_profiles")
        .update(financialProfileUpdates)
        .eq("financialProfileId", financialProfileId);

      if (fpUpdateError) {
        console.error("❌ Erro ao atualizar financial profile:", fpUpdateError);
        throw new Error(`Erro ao atualizar financial profile: ${fpUpdateError.message}`);
      }

      // 2. Atualizar packages, se necessário
      if (shouldUpdatePackage && currentPackageId) {
        if (!packageClientId) {
          throw new Error("ClientId não encontrado para atualizar pacote do paciente.");
        }

        packageUpdates.packageCreatedAt = new Date().toISOString(); // Ou updatedAt, dependendo da necessidade

        const { error: pkgUpdateError } = await supabase
          .from("packages")
          .update(packageUpdates)
          .eq("packageId", currentPackageId)
          .eq("clientId", packageClientId);
        
        if (pkgUpdateError) {
          console.error("❌ Erro ao atualizar package:", pkgUpdateError);
          throw new Error(`Erro ao atualizar package: ${pkgUpdateError.message}`);
        }
      } else if (shouldUpdatePackage && !currentPackageId) {
        // Caso o paciente não tenha um pacote mas estamos tentando adicionar informações de pacote
        // Isso pode indicar que um novo pacote precisa ser criado. Lógica para criar pacote aqui, se necessário.
        console.warn("⚠️ Tentativa de atualizar informações de pacote para paciente sem pacote existente. Considere criar um novo pacote.");
      }

      console.log(`✅ Perfil financeiro do paciente ${patientId} atualizado com sucesso no Supabase.`);

    } catch (error) {
      console.error("Erro ao atualizar perfil financeiro do paciente:", error);
      let errorMessage = "Erro desconhecido ao atualizar perfil financeiro do paciente.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao atualizar perfil financeiro do paciente: ${errorMessage}`);
    }
  }

  static async updatePatientRenewalData(
    patientId: string,
    data: {
      renewalDecisions?: any[];
      renewalStatus?: RenewalStatus;
      obitoDate?: string | null;
    },
    clientId?: string
  ): Promise<void> {
    try {
      const patient = await this.getPatientById(patientId, clientId);
      if (!patient) {
        throw new Error("Paciente não encontrado.");
      }

      const financialProfileId = patient.financialProfile.financialProfileId; 
      const effectiveClientId = clientId || patient.financialProfile.clientId;
      if (!financialProfileId) {
        throw new Error("FinancialProfileId não encontrado para o paciente.");
      }
      if (!effectiveClientId) {
        throw new Error("ClientId não encontrado para atualização de renovação.");
      }

      const updates: Partial<SupabaseFinancialProfileRow> = {};

      if (data.renewalDecisions !== undefined) {
        // renewalDecisions podem ser serializados como JSONB se a coluna existir em financial_profiles
        // Por simplicidade aqui, vamos ignorá-los por enquanto se não houver um lugar no schema.
        //console.warn("⚠️ renewalDecisions não estão sendo persistidos no Supabase no momento. Ajuste o schema ou a lógica se necessário.");
        // Se você decidir persistir renewalDecisions, a coluna 'financialProfileRenewalDecisions' tipo JSONB precisaria ser adicionada a 'financial_profiles'.
        // Ex: updates.financialProfileRenewalDecisions = data.renewalDecisions;
      }

      if (data.renewalStatus !== undefined) {
        updates.financialProfileRenewalStatus = data.renewalStatus;
      }

      if (data.obitoDate !== undefined) {
        updates.financialProfileObitoDate = data.obitoDate || undefined; // Converte null para undefined
      }

      if (Object.keys(updates).length === 0) {
        return;
      }

      updates.financialProfileUpdatedAt = new Date().toISOString();

      const { error } = await supabase
        .from("financial_profiles")
        .update(updates)
        .eq("financialProfileId", financialProfileId)
        .eq("clientId", effectiveClientId);

      if (error) {
        console.error("❌ Erro ao atualizar dados de renovação no Supabase:", error);
        let errorMessage = "Erro desconhecido ao atualizar dados de renovação.";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null && 'message' in error) {
          errorMessage = (error as { message: string }).message;
        }
        throw new Error(`Erro ao atualizar dados de renovação: ${errorMessage}`);
      }

      //console.log(`✅ Dados de renovação atualizados para paciente ${patientId} no Supabase.`);

    } catch (error) {
      console.error("❌ Erro ao atualizar dados de renovação:", error);
      let errorMessage = "Erro desconhecido ao atualizar dados de renovação.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      throw new Error(`Erro ao atualizar dados de renovação: ${errorMessage}`);
    }
  }
}
